import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { useProjectNavigate } from '@/quote/hooks/useProjectNavigate';
import { updateAction } from '@/quote/util/update-action';
import { AIChatWindowChatMessageBoxView, AIChatWindowWelcomeView } from '@jove-frontend/ai';
import { JoveChatMessageResponseModeEnum, JoveChatMessageSenderRoleEnum, JoveProjectTypeEnum } from '@jove-frontend/enums';
import { JoveLogoLoading } from '@jove-frontend/mui';
import { ChatMessagePositionEnum } from '@jove-frontend/types';
import {
  SOCKET_EVENTS,
  SOCKET_QUOTE_API_CHAT_URL_PATH,
  apiGetCoversationAgentOnlineList,
  apiQueryCurrentCoversationChatResponseMode,
  convertLocalInputValueToChatMessageItemInfo,
  covertServerChatPayloadToChatMessageItemInfo,
  getSocket,
  useChatHistoryMessage,
  useSocketChatConnect,
} from '@jove-frontend/uikit';
import { Close } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useStateMachine } from 'little-state-machine';
import { uniqueId } from 'lodash';

const initialWelcomeMessage = covertServerChatPayloadToChatMessageItemInfo({
  body: "Hi, I'm Mia. Feel free to chat with me, and I'll help you get your quote.",
  position: ChatMessagePositionEnum.left,
  sender_role: JoveChatMessageSenderRoleEnum.ai,
  options: [],
});

const CHAT_TRANSITION_DURATION = 250;

const DEFAULT_ANCHOR_RECT = {
  top: 0,
  left: 0,
  width: 414,
  height: 690,
  rightGap: 24,
  bottomGap: 24,
};

type AiChatWidgetProps = HTMLAttributes<HTMLDivElement> & {
  defaultOpen?: boolean;
};

const AiChatWidget = (props: AiChatWidgetProps) => {
  const { defaultOpen } = props;
  const { push } = useProjectNavigate();
  const { state, actions } = useStateMachine({ updateAction });
  const [isOpen, setIsOpen] = useState(!!defaultOpen);
  const [isChatMounted, setIsChatMounted] = useState(false);
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [isLauncherHover, setIsLauncherHover] = useState(false);
  const [chatUserInfo, setChatUserInfo] = useState({
    email: state?.contact?.email || '',
    phone: state?.contact?.phone_number || '',
  });
  const aiChatWindowChatMessageBoxViewRef = useRef<any>(null);
  const [messageList, setMessageList] = useState<any[]>([]);
  const {
    pendingAckRequestIdSetRef,
    setPendingSocketAckCount,
    isSocketConnecting,
    socketConnectErrorMessage,
    pendingSocketAckCount,
    ensureSocketConnected,
    cleanSocketReleateInfo,
    cleanSocketAckReleateInfo,
    isSocketConnected,
  } = useSocketChatConnect({
    onReceiveMessage: receiveSocketMessageAction,
    onReceiveAgentNotificationMessage: receiveSocketMessageAction,
  });
  const { canLoadMoreHistory, isLoadingMoreHistory, historyBefore, requestChatMessageList, requestHistoryChatMessageList } = useChatHistoryMessage();
  const [chatResponseMode, setChatResponseMode] = useState(JoveChatMessageResponseModeEnum.ai);
  const [initialDataLoading, setInitialDataLoading] = useState(false);
  const [hideHumanSupport, setHideHumanSupport] = useState(true);
  const [inputLoading, setInputLoading] = useState(false);
  const [anchorRect, setAnchorRect] = useState(DEFAULT_ANCHOR_RECT);
  const haveSentMessageRef = useRef(false);

  /**
   * requests
   */
  const requestCoversationLatestMessageList = async (item, config?: { before?: string }) => {
    const { list, before } = await requestChatMessageList(item, config);

    const newList = list.map((aItem) => {
      return covertServerChatPayloadToChatMessageItemInfo(aItem);
    });
    setMessageList(newList?.length > 0 ? newList : [initialWelcomeMessage]);

    return {
      list: newList,
      before,
    };
  };

  const requestGetCoversationAgentOnlineList = async () => {
    const [res] = await apiGetCoversationAgentOnlineList({ hideErrToast: true });
    setHideHumanSupport(!res?.data?.data?.length);
  };

  const requestCheckCurrentChatResponseMode = async () => {
    const params = {
      hideErrToast: true,
    };
    const { email, phone } = chatUserInfo;
    if (email) {
      params['email'] = email;
    }
    if (phone) {
      params['phone'] = phone;
    }
    appendChatExtraInfo(params);

    setInputLoading(true);
    const [res] = await apiQueryCurrentCoversationChatResponseMode(params);
    setInputLoading(false);

    const chatMode = !!res?.data?.is_human_mode ? JoveChatMessageResponseModeEnum.agent : JoveChatMessageResponseModeEnum.ai;
    setChatResponseMode(chatMode);
    return chatMode;
  };

  /**
   * effects
   */
  useEffect(() => {
    setChatUserInfo({
      email: state?.contact?.email || '',
      phone: state?.contact?.phone_number || '',
    });
  }, [state?.contact?.email, state?.contact?.phone_number]);

  useEffect(() => {
    if ((chatUserInfo?.email || chatUserInfo?.phone) && isOpen) {
      requestCheckCurrentChatResponseMode();
    }
  }, [chatUserInfo?.email, chatUserInfo?.phone, isOpen]);

  useEffect(() => {
    if (isOpen && messageList) {
      requestGetCoversationAgentOnlineList();
    }
  }, [messageList, isOpen]);

  useEffect(() => {
    const updateAnchorRect = () => {
      const anchorElement = document.querySelector('[data-quote-chat-anchor]');

      if (!anchorElement) {
        setAnchorRect(DEFAULT_ANCHOR_RECT);
        return;
      }

      const rect = anchorElement.getBoundingClientRect();
      const element = anchorElement as HTMLDivElement;

      setAnchorRect({
        top: rect.top,
        left: rect.left,
        width: element.clientWidth || rect.width,
        height: element.clientHeight || rect.height,
        rightGap: Math.max(window.innerWidth - rect.right, 0) + 24,
        bottomGap: Math.max(window.innerHeight - rect.bottom, 0) + 24,
      });
    };

    updateAnchorRect();
    window.addEventListener('resize', updateAnchorRect);
    window.addEventListener('scroll', updateAnchorRect, true);

    return () => {
      window.removeEventListener('resize', updateAnchorRect);
      window.removeEventListener('scroll', updateAnchorRect, true);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsChatMounted(true);

      const frameId = requestAnimationFrame(() => {
        setIsChatVisible(true);
      });

      return () => {
        cancelAnimationFrame(frameId);
      };
    } else {
      setMessageList([]);
    }

    setIsChatVisible(false);

    if (!isChatMounted) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsChatMounted(false);
    }, CHAT_TRANSITION_DURATION);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, isChatMounted]);

  /**
   * methods
   */
  const getSocketConnectConfig = (item) => {
    const auth = {
      phone: item?.phone,
      email: item?.email,
    };
    appendChatExtraInfo(auth);
    const data = {
      url: process.env.NX_CHAT_SOCKET_URL,
      path: SOCKET_QUOTE_API_CHAT_URL_PATH,
      auth,
    };
    return data;
  };

  const chatBoxScrollToBottom = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        aiChatWindowChatMessageBoxViewRef.current?.scrollToBottom?.();
      });
    });
  };

  // Send message handles
  const receiveSocketAckMessage = (payload, requestId: string) => {
    if (!payload || !!payload.error) {
      pendingAckRequestIdSetRef.current.delete(requestId);
      setPendingSocketAckCount((prev) => (prev > 0 ? prev - 1 : 0));
      return;
    }

    if (!pendingAckRequestIdSetRef.current.has(requestId)) {
      return;
    }

    pendingAckRequestIdSetRef.current.delete(requestId);
    setPendingSocketAckCount((prev) => (prev > 0 ? prev - 1 : 0));
    chatBoxScrollToBottom();
  };

  function receiveSocketMessageAction(payload) {
    if (!payload || !!payload.error) {
      return;
    }

    aiChatWindowChatMessageBoxViewRef.current?.appendMessageList([
      covertServerChatPayloadToChatMessageItemInfo(
        {
          ...payload,
          sender_role: !!payload.agent_name ? JoveChatMessageSenderRoleEnum.agent : JoveChatMessageSenderRoleEnum.ai,
          direction: 'outbound-api',
        },
        { position: ChatMessagePositionEnum.left }
      ),
    ]);
    chatBoxScrollToBottom();
  }

  const sendSocketMessage = (messageData, config?: { isReceiveAck?: boolean }) => {
    const socket = getSocket();

    if (!socket?.connected) {
      return false;
    }

    const requestId = uniqueId();
    if (config?.isReceiveAck) {
      pendingAckRequestIdSetRef.current.add(requestId);
      setPendingSocketAckCount((prev) => prev + 1);
    }

    socket.emit(
      SOCKET_EVENTS.sendMessage,
      messageData,
      config?.isReceiveAck
        ? (payload) => receiveSocketAckMessage(payload, requestId)
        : (payload) => {
            console.log('Quote received: ', payload);
          }
    );
    return true;
  };

  const sendMessageAction = async (item) => {
    const chatMode = await requestCheckCurrentChatResponseMode();
    const messageData = {
      body: item?.message,
    };
    appendChatExtraInfo(messageData);

    const isSent = sendSocketMessage(messageData, { isReceiveAck: chatMode === JoveChatMessageResponseModeEnum.ai });
    if (!isSent) {
      return;
    }

    haveSentMessageRef.current = true;
    aiChatWindowChatMessageBoxViewRef.current?.cleanInputInfo();
    aiChatWindowChatMessageBoxViewRef.current?.appendMessageList([
      convertLocalInputValueToChatMessageItemInfo({
        body: item.message,
        email: chatUserInfo?.email,
        phone: chatUserInfo?.phone,
        position: ChatMessagePositionEnum.right,
      }),
    ]);
    chatBoxScrollToBottom();
  };

  // Dialog handles
  const openAIChatAction = () => {
    setIsOpen(true);

    if (!chatUserInfo?.email && !chatUserInfo?.phone) {
      return;
    }

    if (isSocketConnected()) {
      return;
    }

    void (async () => {
      setInitialDataLoading(true);
      const isConnected = await ensureSocketConnected(getSocketConnectConfig(chatUserInfo));

      if (!isConnected) {
        setInitialDataLoading(false);
        return;
      }

      await requestCoversationLatestMessageList(chatUserInfo);
      chatBoxScrollToBottom();
      setInitialDataLoading(false);
    })();
  };

  const closeAIChatAction = () => {
    if (haveSentMessageRef.current) {
      let quoteId = '';
      for (let i = messageList.length - 1; i >= 0; i--) {
        if (!quoteId && messageList[i].entity_name === 'quote' && messageList[i].entity_id) {
          quoteId = messageList[i].entity_id;
          break;
        }
      }
      if (quoteId) {
        push({ path: `/q/${quoteId}`, replace: true });
      }
    }
    setIsOpen(false);
    cleanSocketAckReleateInfo();
    cleanSocketReleateInfo();
  };

  const getYourQuoteAction = async (item) => {
    const isConnected = await ensureSocketConnected(getSocketConnectConfig(item));

    if (!isConnected) {
      return;
    }

    setChatUserInfo((prev) => ({
      ...prev,
      ...item,
    }));

    setInitialDataLoading(true);
    await requestCoversationLatestMessageList(item);
    chatBoxScrollToBottom();
    setInitialDataLoading(false);
  };

  const logoutAction = () => {
    cleanSocketAckReleateInfo();
    cleanSocketReleateInfo();
    setChatUserInfo({
      email: '',
      phone: '',
    });
    setMessageList([]);
    setChatResponseMode(JoveChatMessageResponseModeEnum.ai);
  };

  const buttonClickAction = (value) => {
    const messageData = {
      message: value,
    };
    sendMessageAction(messageData);
  };

  const messageListChangeAction = (list) => {
    setMessageList(list);
  };

  const appendChatExtraInfo = (params) => {
    if (messageList?.length > 0) {
      const item = messageList.find((aItem) => !!aItem.session_id);
      params['session_id'] = item?.session_id;
    }
    if (state.quoteId) {
      params['entity_id'] = state.quoteId;
    }
    params['entity_name'] = JoveProjectTypeEnum.quote;
    params['client_from'] = JoveProjectTypeEnum.quote;
  };

  const chatModeChangeAction = (value) => {
    setChatResponseMode(value);
  };

  // History messages handles
  const loadHistoryMessagesAction = async () => {
    if (isLoadingMoreHistory || !canLoadMoreHistory) {
      return;
    }

    const { list } = await requestHistoryChatMessageList(chatUserInfo, { before: historyBefore });

    const newList = list.map((aItem) => {
      return covertServerChatPayloadToChatMessageItemInfo(aItem);
    });

    if (newList.length > 0) {
      aiChatWindowChatMessageBoxViewRef.current?.prependMessageList(newList);
    }
  };

  return (
    <>
      {!isChatMounted && (
        <Box
          className="flex cursor-pointer flex-row items-center space-x-1"
          onClick={openAIChatAction}
          onMouseEnter={() => setIsLauncherHover(true)}
          onMouseLeave={() => setIsLauncherHover(false)}
        >
          <img
            src="https://storage.googleapis.com/jove-public-images/ai/ai-avatar.png"
            className="h-8 w-8 rounded-full"
            style={{
              transform: isLauncherHover ? 'scale(1.12)' : 'scale(1)',
              transition: 'transform 200ms ease-out',
            }}
            alt="AI avatar"
          />
          <Typography variant="subtitle2" className="text-xs">
            Talk to Jove Assistant
          </Typography>
        </Box>
      )}

      {isChatMounted && (
        <Box
          className="fixed z-[100002] flex flex-col overflow-hidden bg-white shadow-2xl transition-all duration-300 ease-out"
          style={{
            top: `${anchorRect.top}px`,
            left: `${anchorRect.left}px`,
            width: `${anchorRect.width}px`,
            height: `${anchorRect.height}px`,
            opacity: isChatVisible ? 1 : 0,
            transform: isChatVisible ? 'translateY(0)' : 'translateY(16px)',
            pointerEvents: isChatVisible ? 'auto' : 'none',
          }}
        >
          {initialDataLoading && <JoveLogoLoading />}
          <Box className="relative flex items-center justify-end bg-blue-600 px-3 py-4 text-[#202020]">
            <Box className="absolute left-0 top-0 flex h-full w-full flex-row items-center justify-center">
              <Typography variant="h5" className="font-bold">
                Hi, I'm Mia
              </Typography>
            </Box>
            <Box className="flex flex-row items-center space-x-2">
              <Close className="z-20 h-6 w-6 cursor-pointer" onClick={closeAIChatAction} />
            </Box>
          </Box>

          <Box className="flex h-0 flex-1 overflow-hidden">
            {chatUserInfo?.email ? (
              <AIChatWindowChatMessageBoxView
                ref={aiChatWindowChatMessageBoxViewRef}
                className="h-full w-full bg-[#F8FAFC]"
                chatUserInfo={chatUserInfo}
                sendMessage={sendMessageAction}
                buttonClick={buttonClickAction}
                showAnalyzingIndicator={pendingSocketAckCount > 0}
                list={messageList}
                messageListChange={messageListChangeAction}
                chatModeChange={chatModeChangeAction}
                onReachTop={loadHistoryMessagesAction}
                canLoadMoreHistory={canLoadMoreHistory}
                isLoadingMoreHistory={isLoadingMoreHistory}
                hideHumanSupport={hideHumanSupport}
                chatResponseMode={chatResponseMode}
                inputLoading={inputLoading}
              />
            ) : (
              <AIChatWindowWelcomeView
                className="h-full w-full"
                getYourQuote={getYourQuoteAction}
                isLoading={isSocketConnecting}
                connectionErrorMessage={socketConnectErrorMessage}
                country={state?.contact?.country}
              />
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default AiChatWidget;
